import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { ReasonForEditsType } from 'modals/ShiftModal/src/views/ReasonForEdits/ReasonForEdits.types';
import { useFormBehaviors } from 'hooks';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useTranslation } from 'hooks/useTranslation';

import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  API_PATCH_updateOrderDetails,
  OrdersSkillsAttribute,
  UpdateOrderDetailsParams,
} from 'requests/PATCH_updateOrderDetails';
import { toastService } from 'services';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { OpenShiftPolicyEnum, OrderStatusEnum, CustomField, Order } from 'types/graphql';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { calculateDuration, convertTimeToHours } from 'utils/time';
import {
  VALIDATION_customerSelection,
  VALIDATION_durationMin1Hr,
  VALIDATION_durationMin1HrNoMessage,
} from 'modals/ShiftModal/src/views/EditView/EditView.validation.constants';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import {
  isEditCompletedShiftsFeatureEnabledSelector,
  isEditInProgressShiftsFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import { ICustomer } from 'pages/Schedule/components/CustomersDropdown/CustomersDropdown.types';
import { composeCustomFieldsForPayload } from 'modals/ShiftModal/src/components/ShiftCustomFields/ShiftCustomFields.utils';

type EditViewContextType = {
  totalDuration: string;
  totalPaidDuration: string;
  isNotifyFlowRequired: boolean;
  isEditInProgressShift: boolean;
  isEditPostShift: boolean;
  hasCustomers: boolean;
  customers?: ICustomer[];
  selectedCustomer?: ICustomer;
  currentOpenShiftPolicy: OpenShiftPolicyEnum | null;
  setCurrentOpenShiftPolicy: Dispatch<SetStateAction<OpenShiftPolicyEnum | null>>;
};

interface StaticEditViewFormType {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  communityCustomFields?: any;
  start_date: string[];
  start_time: string[];
  end_time: string[];
  unpaid_break_time: string[];
  description: string;
  allow_overtime: boolean;
  auto_extend_bid: boolean;
  notification_method?:
    | 'needs_acknowledgement'
    | 'reaccept_or_decline'
    | 'only_notification'
    | 'no_notification';
  reaccept_or_decline_threshold: Array<'4' | '12' | '24' | '48' | '72'>;
  reasonForEdit: ReasonForEditsType[];
  specifiedReasonForEdit?: string;
  zeroHoursWorked?: boolean;
  status: OrderStatusEnum;
  acceptedByAgency?: boolean;
  customer?: number;
  hasCustomers?: boolean;
  positionId: number[];
  positionName: string;
  byMultipleFields: string;
  teamMemberName: string;
  janeId: string;
  required_skills: string[];
}

export interface EditViewFormType extends StaticEditViewFormType {
  [key: string]:
    | string
    | string[]
    | boolean
    | number
    | number[]
    | undefined
    | { id: string; name: string };
}

export const EditViewContext = createContext({} as EditViewContextType);

export const EditViewProvider: FC = ({ children }) => {
  const { data } = useShiftModalDataContext();
  const order = data?.order;
  const { refreshBackgroundPage } = useShiftModalPropsContext();
  const { apiRequest } = useAPIActions();

  const { t } = useTranslation();
  const { timeZone, value: locationIdRaw } = getCurrentlySelectedLocation();

  const start_time = DateTime.fromISO(order!.startAt).setZone(timeZone).toFormat('HH:mm:ss');
  const end_time = DateTime.fromISO(order!.endAt).setZone(timeZone).toFormat('HH:mm:ss');

  const customers = useMemo(() => {
    if (!order!.community?.subFacilities) return [];

    return order!.community?.subFacilities.flatMap((subFacility) => {
      return subFacility.subFacilityAddresses
        .filter((address) => address.displayId != null)
        .map((address) => {
          const displayId = address.displayId ?? undefined;
          return {
            id: address.displayId!,
            name: address.name,
            address: address.address,
            contactPhone: address.contactPhone,
            customerName: subFacility.name,
            subFacilityAddressId: displayId,
          };
        });
    });
  }, [order!.community]);

  const hasCustomers = customers?.length > 0;

  const [currentOpenShiftPolicy, setCurrentOpenShiftPolicy] = useState<OpenShiftPolicyEnum | null>(
    order!.position?.openShiftPolicy || null,
  );

  const initialState: StaticEditViewFormType = useMemo(() => {
    return {
      start_time: [start_time],
      end_time: [end_time],
      unpaid_break_time: [order!.orderSchedule.breakTime],
      start_date: [DateTime.fromISO(order!.startDate).setZone(timeZone).toString()],
      description: order!.description || '',
      notification_method: undefined,
      reaccept_or_decline_threshold: ['24'],
      allow_overtime: order!.allowOvertime,
      auto_extend_bid: order!.autoRebid,
      reasonForEdit: [],
      specifiedReasonForEdit: '',
      zeroHoursWorked: order.noShow,
      status: order!.status?.value,
      acceptedByAgency: order!.mainJane?.acceptedByAgency,
      communityCustomFields: order!.availableCustomFields || '',
      customer: order!.subFacilityAddress?.displayId || undefined,
      hasCustomers: hasCustomers,
      positionId: [order!.position.displayId],
      positionName: order!.position.name,
      byMultipleFields: order!.mainJane
        ? `${order!.mainJane.firstName} ${order!.mainJane.lastName}`
        : '',
      teamMemberName: order!.mainJane
        ? `${order!.mainJane.firstName} ${order!.mainJane.lastName}`
        : '',
      janeId: order!.mainJane?.id || '',
      required_skills: order!.ordersSkills?.map(({ skill }) => `${skill.displayId}`) ?? [],
    };
  }, [data?.order, end_time, start_time, timeZone, hasCustomers]);

  const communityCustomFields = order.availableCustomFields;
  const orderCustomFields = order.customFields;

  const initialStateWithCustomFields = useMemo(
    () => ({
      ...initialState,
      ...communityCustomFields?.reduce((acc, customField) => {
        const orderCustomField = orderCustomFields.find(
          (orderCustomField) => orderCustomField.customFieldId === customField.id,
        );
        const isDropdownAndPrefillDefault =
          customField.fieldType === 'dropdown' && customField.prefillDefault;
        const dropdownDefaultOptionValue = customField.customFieldOptions?.length
          ? customField.customFieldOptions[0]?.value || ''
          : '';
        return {
          ...acc,
          [customField.name]: isDropdownAndPrefillDefault
            ? orderCustomField?.value || dropdownDefaultOptionValue
            : orderCustomField?.value || (customField.fieldType === 'dropdown' ? [''] : ''),
        };
      }, {}),
    }),
    [communityCustomFields, initialState, orderCustomFields],
  );

  const locationId = Number.parseInt(locationIdRaw);

  const isEditInProgressShiftsFeatureEnabled = useSelector(
    isEditInProgressShiftsFeatureEnabledSelector(locationId),
  );

  const isEditCompletedShiftsFeatureEnabled = useSelector(
    isEditCompletedShiftsFeatureEnabledSelector(locationId),
  );

  const updateOrderDetails = useCallback(
    async (values) => {
      const orderDisplayId = data?.order!.id;
      if (!orderDisplayId) return;
      await apiRequest(
        API_PATCH_updateOrderDetails(
          payloadTransformer({
            formValues: values,
            initialValues: initialStateWithCustomFields,
            shift_id: orderDisplayId,
            order: data!.order,
          }),
        ),
        {
          onSuccess: () => {
            toastService.success(t('success:SHIFT_EDITED'));
            refreshBackgroundPage();
            shiftModalRoutingService.close();
          },
          onError: handleGenericErrorMessage,
        },
      );
    },
    [apiRequest, data?.order, initialStateWithCustomFields, t, refreshBackgroundPage],
  );

  const formUtils = useFormBehaviors<EditViewFormType>({
    initialState: initialStateWithCustomFields,
    validations: {
      end_time: VALIDATION_durationMin1Hr,
      start_time: VALIDATION_durationMin1HrNoMessage,
      unpaid_break_time: VALIDATION_durationMin1HrNoMessage,
      customer: VALIDATION_customerSelection,
    },
    onSubmit: updateOrderDetails,
    isDirtyCheckEnabled: false,
    type: 'EDIT',
  });

  const _handleChange = useCallback(
    (event) => {
      formUtils.onChange(event);
    },
    [formUtils],
  );

  const totalDuration = calculateDuration(
    formUtils?.values?.start_time[0],
    formUtils?.values?.end_time[0],
    formUtils?.values?.start_date[0],
    convertTimeToHours(formUtils?.values?.unpaid_break_time[0]),
    'pretty',
    false,
  ) as string;

  const totalPaidDuration = calculateDuration(
    formUtils.values.start_time[0],
    formUtils.values.end_time[0],
    formUtils?.values?.start_date[0],
    convertTimeToHours(formUtils?.values?.unpaid_break_time[0]),
    'pretty',
    true,
  ) as string;

  const value: EditViewContextType = useMemo(() => {
    const customFieldKeys = communityCustomFields.map((field) => field.name);

    const selectedCustomer = customers?.find(
      (customer) => customer.id === formUtils.values.customer,
    );

    return {
      ...formUtils,
      onChange: _handleChange,
      totalDuration,
      totalPaidDuration,
      isNotifyFlowRequired: getIsNotifyFlowRequired(
        initialState,
        formUtils.values,
        customFieldKeys,
      ),
      isEditInProgressShift:
        isEditInProgressShiftsFeatureEnabled &&
        order!.status?.value === OrderStatusEnum.in_progress,
      isEditPostShift:
        isEditCompletedShiftsFeatureEnabled && order!.status?.value === OrderStatusEnum.completed,
      customers,
      hasCustomers,
      selectedCustomer,
      currentOpenShiftPolicy,
      setCurrentOpenShiftPolicy,
    };
  }, [
    _handleChange,
    order!.status?.value,
    formUtils,
    initialState,
    isEditCompletedShiftsFeatureEnabled,
    isEditInProgressShiftsFeatureEnabled,
    totalDuration,
    totalPaidDuration,
    communityCustomFields,
    hasCustomers,
    customers,
    currentOpenShiftPolicy,
  ]);

  return <EditViewContext.Provider value={value}>{children}</EditViewContext.Provider>;
};

export const useEditViewContext = (): EditViewContextType & UseFormBehaviors<EditViewFormType> =>
  useContext(EditViewContext) as EditViewContextType & UseFormBehaviors<EditViewFormType>;

function getIsNotifyFlowRequired(
  initialState: StaticEditViewFormType,
  values: EditViewFormType,
  customFieldKeys: string[],
) {
  if (
    initialState.positionId[0] !== values.positionId[0] ||
    initialState.byMultipleFields !== values.byMultipleFields
  ) {
    return false;
  }

  let keysUpdated: string[] = [];
  const keys = Object.keys(initialState) as Array<keyof StaticEditViewFormType>;

  const initialCustomFields = initialState.communityCustomFields || '[]';

  keys.forEach((key) => {
    if (key === 'communityCustomFields') {
      customFieldKeys.forEach((el) => {
        if (values[el]) {
          const initialField = initialCustomFields.find(
            (field: { name: string }) => field.name === el,
          );

          if (initialField?.value !== values[el]) {
            keysUpdated.push(el);
          }
        }
      });
    } else {
      if (Array.isArray(initialState[key])) {
        const initial = initialState[key] as string[];
        const value = values[key] as string[];
        if (initial.sort().join() !== value.sort().join()) keysUpdated.push(key);
      } else {
        if (initialState[key] !== values[key]) keysUpdated.push(key);
      }
    }
  });
  const isNotifyFlowRequired = keysUpdated.some((key) => {
    const keysThatRequireNotifyFlow = [
      'start_time',
      'end_time',
      'unpaid_break_time',
      'start_date',
      'zeroHoursWorked',
      'allow_overtime',
      'auto_extend_bid',
      'description',
      'customer',
      ...customFieldKeys,
    ];
    return keysThatRequireNotifyFlow.includes(key);
  });

  return isNotifyFlowRequired;
}

export const getOrdersSkillsAttributes = (
  selectedSkillIds: string[],
  existingOrdersSkills: Order['ordersSkills'],
): OrdersSkillsAttribute[] => {
  const existingSkillIds = existingOrdersSkills.map(({ skill }) => `${skill.displayId}`);
  const addedSkills = selectedSkillIds
    .filter((skillId) => !existingSkillIds.includes(skillId))
    .map((skillId) => ({ skill_id: Number(skillId) }));
  const removedSkills = existingOrdersSkills
    .filter(({ skill }) => !selectedSkillIds.includes(`${skill.displayId}`))
    .map(({ displayId }) => ({ id: displayId, _destroy: true as const }));
  return [...addedSkills, ...removedSkills];
};

function payloadTransformer({
  formValues,
  initialValues,
  shift_id,
  order,
}: {
  formValues: EditViewFormType;
  initialValues: EditViewFormType;
  shift_id: number;
  order: Order;
}): UpdateOrderDetailsParams {
  const communityCustomFields: CustomField[] = order.availableCustomFields || [];
  const currentPositionId = order.position.displayId;
  const currentJaneId = order.mainJane?.id || '';
  const biddable = order.biddable;
  const sentToAgencies = order.sentToAgencies;

  const payload: UpdateOrderDetailsParams = {
    shift_id,
  };

  // Helper to check if array values changed
  const arrayChanged = (key: keyof EditViewFormType) => {
    const initial = initialValues[key] as string[];
    const current = formValues[key] as string[];
    return initial?.sort().join() !== current?.sort().join();
  };

  // Helper to check if value changed
  const valueChanged = (key: keyof EditViewFormType) => {
    return initialValues[key] !== formValues[key];
  };

  // If start date or start time has changed, adjust start_date_time accordingly
  if (arrayChanged('start_time') || arrayChanged('start_date')) {
    const [start_hour, start_minutes] = formValues.start_time[0].split(':');
    const _start_time = DateTime.fromISO(formValues.start_date[0]).set({
      hour: parseInt(start_hour),
      minute: parseInt(start_minutes),
    });

    payload.start_date_time = {
      date: _start_time.toFormat('yyyy-MM-dd'),
      hour: _start_time.get('hour'),
      minute: _start_time.get('minute'),
    };
  }

  // If start date or end time has changed, adjust end_date_time accordingly
  if (arrayChanged('end_time') || arrayChanged('start_date')) {
    const [end_hour, end_minutes] = formValues.end_time[0].split(':');
    const [start_hour, start_minutes] = formValues.start_time[0].split(':');
    const _start_time = DateTime.fromISO(formValues.start_date[0]).set({
      hour: parseInt(start_hour),
      minute: parseInt(start_minutes),
    });
    let _end_time = DateTime.fromISO(formValues.start_date[0]).set({
      hour: parseInt(end_hour),
      minute: parseInt(end_minutes),
    });

    if (_start_time > _end_time) {
      _end_time = _end_time.plus({ day: 1 });
    }

    payload.end_date_time = {
      date: _end_time.toFormat('yyyy-MM-dd'),
      hour: _end_time.get('hour'),
      minute: _end_time.get('minute'),
    };
  }

  if (arrayChanged('unpaid_break_time')) {
    payload.unpaid_break_time = formValues.unpaid_break_time[0];
  }

  if (valueChanged('description')) {
    payload.description = formValues.description;
  }

  if (valueChanged('allow_overtime')) {
    payload.allow_overtime = !!formValues.allow_overtime;
  }

  if (valueChanged('auto_extend_bid')) {
    payload.auto_rebid = !!formValues.auto_extend_bid;
  }

  if (valueChanged('zeroHoursWorked')) {
    payload.zero_hours_worked = !!formValues.zeroHoursWorked;
  }

  if (valueChanged('customer')) {
    payload.sub_facility_address_id = `${formValues.customer}`;
  }

  if (arrayChanged('positionId')) {
    payload.position_id = formValues.positionId[0]?.toString();
  }

  if (valueChanged('janeId')) {
    payload.jane_id =
      formValues.janeId && formValues.janeId.trim() !== '' ? formValues.janeId : null;
  }

  const customFields = composeCustomFieldsForPayload(formValues, communityCustomFields);
  if (customFields.length > 0) {
    payload.custom_fields = customFields;
  }

  // Handle notification method logic
  const isReassign =
    formValues.positionId?.[0] !== currentPositionId || formValues.janeId !== currentJaneId;

  if (formValues.notification_method) {
    payload.notification_method = formValues.notification_method;
  } else if (biddable) {
    payload.notification_method =
      formValues.status === OrderStatusEnum.pending ? 'no_notification' : 'only_notification';
  } else if (sentToAgencies) {
    payload.notification_method =
      formValues.status === OrderStatusEnum.requested ||
      formValues.status === OrderStatusEnum.pending
        ? 'no_notification'
        : 'only_notification';
  } else if (
    formValues.status === OrderStatusEnum.completed ||
    formValues.status === OrderStatusEnum.in_progress
  ) {
    payload.notification_method = 'no_notification';
  }

  if (isReassign) {
    if (formValues.janeId) {
      payload.notification_method = 'only_notification';
    } else {
      payload.notification_method = 'no_notification';
    }
  } else if (formValues.reasonForEdit?.[0]) {
    payload.reason_category = formValues.reasonForEdit[0];
    payload.reason_message = formValues.specifiedReasonForEdit;
  }

  if (formValues.reaccept_or_decline_threshold) {
    payload.reaccept_or_decline_threshold = Number.parseInt(
      formValues.reaccept_or_decline_threshold[0],
      10,
    );
  }

  // Always include status and acceptedByAgency if present
  if (formValues.status !== undefined) {
    payload.status = formValues.status;
  }

  if (formValues.acceptedByAgency !== undefined) {
    payload.acceptedByAgency = formValues.acceptedByAgency;
  }

  const ordersSkillsAttributes = getOrdersSkillsAttributes(
    formValues.required_skills,
    order.ordersSkills ?? [],
  );
  if (ordersSkillsAttributes.length) {
    payload.orders_skills_attributes = ordersSkillsAttributes;
  }

  return payload;
}
